import { z } from 'zod';
import type { BlockDefinition } from '../types';

export const counterStatsSchema = z.object({
  variant: z.enum(['dark', 'light', 'accent']).default('dark'),
  eyebrow: z.string().max(120).optional(),
  h2: z.string().max(200).optional(),
  subtitle: z.string().max(400).optional(),
  items: z
    .array(
      z.object({
        value: z.string().min(1).max(40),
        prefix: z.string().max(20).optional(),
        suffix: z.string().max(20).optional(),
        label: z.string().min(1).max(160),
        sublabel: z.string().max(160).optional(),
      }),
    )
    .min(2)
    .max(6),
});

export type CounterStatsData = z.infer<typeof counterStatsSchema>;

export function CounterStatsRenderer({ variant = 'dark', eyebrow, h2, subtitle, items }: CounterStatsData) {
  const safe = Array.isArray(items) ? items : [];
  return (
    <section className={`v5-counter v5-counter-${variant}`}>
      <div className="container">
        {(eyebrow || h2) && (
          <div className="v5-section-head">
            {eyebrow ? <div className="v5-eyebrow">{eyebrow}</div> : null}
            {h2 ? <h2 className="v5-h2">{h2}</h2> : null}
            {subtitle ? <p className="v5-lead">{subtitle}</p> : null}
          </div>
        )}
        <div className="v5-counter-grid" data-count={safe.length}>
          {safe.map((it, i) => (
            <div key={i} className="v5-counter-item">
              <div className="v5-counter-number">
                {it.prefix ? <span className="v5-counter-affix">{it.prefix}</span> : null}
                <span data-counter-value={it.value}>{it.value}</span>
                {it.suffix ? <span className="v5-counter-affix">{it.suffix}</span> : null}
              </div>
              <div className="v5-counter-label">{it.label}</div>
              {it.sublabel ? <div className="v5-counter-sublabel">{it.sublabel}</div> : null}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export const counterStatsBlock: BlockDefinition<CounterStatsData> = {
  meta: {
    type: 'counter-stats',
    label: 'Stats animadas',
    description: 'Bloque de números grandes con prefijo/sufijo (€, +, %) y etiqueta. Animables vía data-counter.',
    category: 'social-proof',
    icon: 'bar-chart-3',
  },
  schema: counterStatsSchema,
  defaults: {
    variant: 'dark',
    eyebrow: 'POR LOS NÚMEROS',
    h2: 'Cifras que importan',
    items: [
      { prefix: '+', value: '1.200', label: 'Clientes activos', sublabel: 'en 12 países' },
      { prefix: '€', value: '115M', suffix: '+', label: 'Movilizados', sublabel: 'desde 2009' },
      { value: '99,9', suffix: '%', label: 'Uptime', sublabel: 'últimos 12 meses' },
      { value: '4,8', suffix: '/5', label: 'Satisfacción', sublabel: 'NPS verificado' },
    ],
  },
  fields: [
    {
      name: 'variant',
      label: 'Variante',
      type: 'select',
      options: [
        { label: 'Oscura', value: 'dark' },
        { label: 'Clara', value: 'light' },
        { label: 'Acento', value: 'accent' },
      ],
    },
    { name: 'eyebrow', label: 'Eyebrow', type: 'text', max: 120 },
    { name: 'h2', label: 'Título', type: 'text', max: 200 },
    { name: 'subtitle', label: 'Subtítulo', type: 'textarea', max: 400 },
    {
      name: 'items',
      label: 'Items (2-6)',
      type: 'array',
      max: 6,
      itemFields: [
        { name: 'prefix', label: 'Prefijo', type: 'text', max: 20, placeholder: '€, +, ↑' },
        { name: 'value', label: 'Valor (animable)', type: 'text', required: true, max: 40, placeholder: '1.200' },
        { name: 'suffix', label: 'Sufijo', type: 'text', max: 20, placeholder: '%, +, /5' },
        { name: 'label', label: 'Etiqueta', type: 'text', required: true, max: 160 },
        { name: 'sublabel', label: 'Sub-etiqueta', type: 'text', max: 160 },
      ],
    },
  ],
  Renderer: CounterStatsRenderer,
};
